import InfinityIcon from "@/assets/colorable/infinity.svg?react";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import SourceContentIcon from "@/assets/colorable/source_content.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import UgcIcon from "@/assets/colorable/ugc.svg?react";
import Button from "@/components/Common/Button";
import CardForm from "@/components/Common/payment/CardForm";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import type {InvoiceSerializer} from "@/generated/DjangoTypes";
import type {TFailedInvoicePayment} from "@/hooks/useFailedInvoicePayment";
import {getPackageIcon, packages} from "@/hooks/usePackages";
import {getPaymentDeadline, usePayment} from "@/hooks/usePayment";
import clsxm from "@/lib/clsxm";
import CardComponent from "@/pages/profile/CardComponent";
import {Can} from "@/permissions";
import {CARD_BRANDS} from "@/store/constants";
import type {TBillingDetails, TPackageName} from "@/store/types";
import dayjs from "dayjs";
import {findKey} from "lodash";
import type {FC} from "react";
import {useTranslation} from "react-i18next";

const PaymentFailedContent: FC<{
  actionButtonClassName?: string;
  billingDetails: TBillingDetails;
  dividerClassName?: string;
  headingClassName?: string;
  invoices: InvoiceSerializer[];
  payment: TFailedInvoicePayment;
}> = ({actionButtonClassName, billingDetails, dividerClassName, headingClassName, invoices, payment}) => {
  const {t} = useTranslation(["common", "settings"]);
  const {getTranslatedErrorMessage} = usePayment();
  const deadline = getPaymentDeadline(invoices);
  const {
    currency,
    invoiceIds,
    isLoadingPaymentMethods,
    paymentMethods,
    selectedPaymentMethodId,
    setSelectedPaymentMethodId,
    setShowAddNewPaymentMethodForm,
    setShowUpdatePaymentMethodForm,
    showAddNewPaymentMethodForm,
    showUpdatePaymentMethodForm,
  } = payment;

  return (
    <>
      <div className="flex flex-col gap-5">
        <span className={clsxm("text-black-bluetint text-2.5xl leading-8 font-semibold", headingClassName)}>
          {t("whileProcessingYourPaymentFor")}:
        </span>
        <table className="text-neutral-black w-full text-left text-xs">
          <thead className="bg-neutral-grey-lightest text-neutral-grey-dark rounded-md text-xs font-semibold uppercase">
            <tr>
              <th className="border-neutral-white-dark border-y border-l px-3 py-2.5 tracking-wider">{t("item")}</th>
              <th className="border-neutral-white-dark border-y px-3 py-2.5 tracking-wider">{t("Description")}</th>
              <th className="border-neutral-white-dark border-y px-3 py-2.5 tracking-wider">{t("dateBilled")}</th>
              <th className="border-neutral-white-dark border-y border-r px-3 py-2.5 tracking-wider">
                {t("price")} ({currency})
              </th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(invoice => {
              const plan =
                invoice.invoice_type === "PACKAGE_SUBSCRIPTION"
                  ? (findKey(packages, {
                      type: invoice.invoice_package_subscription?.name,
                    }) as TPackageName)
                  : null;

              return (
                <tr key={invoice.id} className="border-neutral-white-dark border-b bg-white hover:bg-neutral-100">
                  <td className="p-3">
                    {invoice.invoice_type === "UGC_CAMPAIGN" ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <UgcIcon className="h-5 w-auto shrink-0" />
                        <span className="text-neutral-black text-base font-semibold">
                          {invoice.campaign ? invoice.campaign.title : "-"}
                        </span>
                      </span>
                    ) : invoice.invoice_type == "PACKAGE_SUBSCRIPTION" ? (
                      <span className="flex flex-wrap items-center gap-1">
                        {getPackageIcon({packageType: plan!, className: "h-5 w-auto shrink-0"})}
                        <span className="text-neutral-black text-base font-semibold">
                          {t(packages[plan!]?.translationKey as "packageAdvanced")} {t("package")}
                        </span>
                      </span>
                    ) : invoice.invoice_type === "BUNDLE_BUYOUTS" ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <SourceContentIcon className="text-neutral-black size-4" />
                        <span className="text-neutral-black text-base font-semibold">
                          {t("xContentBuyouts", {
                            count: Number(invoice.buyout_count),
                          })}
                        </span>
                      </span>
                    ) : (
                      <span className="flex flex-wrap items-center gap-2">
                        {invoice.campaign?.campaign_type === "Instagram" && (
                          <InstagramIcon className="h-5 w-auto shrink-0" />
                        )}
                        {invoice.campaign?.campaign_type === "TikTok" && <TiktokIcon className="h-5 w-auto shrink-0" />}
                        {invoice.campaign?.on_going && !invoice.campaign.event && (
                          <span className="border-neutral-black h-5 shrink-0 rounded-full border px-2">
                            <InfinityIcon className="h-full w-auto shrink-0" />
                          </span>
                        )}
                        <span className="text-neutral-black text-base font-semibold">
                          {invoice.campaign ? invoice.campaign.title : "-"}
                        </span>
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-2.5 p-3">
                      {invoice.invoice_type === "PACKAGE_SUBSCRIPTION" && (
                        <span className="flex items-center gap-1.5">
                          {(invoice.invoice_package_subscription?.package_category === "INSTAGRAM" ||
                            invoice.invoice_package_subscription?.package_category === "BOTH") && (
                            <InstagramIcon className="size-3 shrink-0" />
                          )}
                          {(invoice.invoice_package_subscription?.package_category === "TIKTOK" ||
                            invoice.invoice_package_subscription?.package_category === "BOTH") && (
                            <TiktokIcon className="size-3 shrink-0" />
                          )}
                        </span>
                      )}
                      {invoice.description}
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-wrap items-center gap-1 p-3">
                      <span className="text-sm font-semibold">{dayjs(invoice.created).format("MMM DD, YYYY")}</span>
                    </div>
                  </td>
                  <td className="p-3 text-sm font-semibold">{invoice.amount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="mt-5 flex flex-wrap items-center gap-3 sm:gap-5">
          {paymentMethods?.[0] && (
            <Can I="list" a="CompanyPaymentMethod">
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  checked={selectedPaymentMethodId === paymentMethods[0].id}
                  onChange={() => setSelectedPaymentMethodId(paymentMethods[0].id)}
                />
                <div className="flex items-center gap-2 rounded-full border px-3 py-1.5">
                  <img
                    src={CARD_BRANDS[
                      paymentMethods[0].type === "card" && paymentMethods[0].card
                        ? paymentMethods[0].card.brand
                        : paymentMethods[0].type
                    ].toLowerCase()}
                    className="h-4.5 shrink-0"
                  />
                </div>
                {paymentMethods[0].type === "card" && (
                  <span className="text-sm font-medium">**** {paymentMethods[0].card?.last4}</span>
                )}
              </div>
            </Can>
          )}
          {invoices[0] && (
            <div className="flex flex-wrap items-center gap-3">
              <span className={clsxm("text-black-bluetint text-2.5xl leading-8 font-semibold", headingClassName)}>
                {t("failedBecause")}:
              </span>
              <span className="bg-danger-light-red text-neutral-black text-2xs flex h-6 items-center rounded-full px-2 font-semibold uppercase">
                {getTranslatedErrorMessage(invoices[0])}
              </span>
              {deadline && (
                <span className="border-neutral-grey-light-dark text-neutral-black text-2xs flex h-6 items-center rounded-full border bg-white px-2 font-semibold uppercase">
                  {t("settings:make_payment_by", {date: deadline.format("MMMM Do")})}
                </span>
              )}
            </div>
          )}
        </div>
        <Can I="create" a="CompanyPaymentMethod">
          {paymentMethods?.[0]?.type === "card" && !showUpdatePaymentMethodForm && (
            <Button
              onClick={() => {
                setShowUpdatePaymentMethodForm(true);
                setShowAddNewPaymentMethodForm(false);
              }}
              buttonStyle="Blue"
              className={clsxm("h-auto w-fit px-8 py-3", actionButtonClassName)}>
              {t("updateCardInformation")}
            </Button>
          )}
          {paymentMethods?.[0]?.type === "card" && showUpdatePaymentMethodForm && (
            <CardForm
              buttonLabel={t("updateCardInformation")}
              invoiceIds={invoiceIds}
              onSubmit={() => setShowUpdatePaymentMethodForm(false)}
              onCancel={() => setShowUpdatePaymentMethodForm(false)}
            />
          )}
        </Can>
        <div className={clsxm("bg-neutral-grey-light-dark -mx-5 h-px", dividerClassName)} />

        {isLoadingPaymentMethods ? (
          <>
            <SkeletonLoader className={clsxm("h-8 max-w-72", headingClassName && "h-6 max-w-40")} />
            {Array.from({length: 2}).map((_, index) => (
              <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:gap-0" key={index}>
                <div className="flex items-center gap-2.5">
                  <SkeletonLoader className="size-4 shrink-0 rounded-full" />
                  <SkeletonLoader className="h-7 w-14 shrink-0 rounded-full" />
                  <SkeletonLoader className="h-5 w-20 shrink-0" />
                </div>
                <div className="flex items-center gap-3">
                  <SkeletonLoader className="h-7 w-36 shrink-0 rounded-md" />
                  <SkeletonLoader className="size-8 shrink-0 rounded-md" />
                </div>
              </div>
            ))}
          </>
        ) : paymentMethods?.length! > 1 ? (
          <Can I="list" a="CompanyPaymentMethod">
            <span className={clsxm("text-black-bluetint text-2.5xl leading-8 font-semibold", headingClassName)}>
              {t("use_other_payment_method")}
            </span>
            {paymentMethods?.slice(1)?.map(paymentMethod => (
              <CardComponent
                key={paymentMethod.id}
                billingDetails={billingDetails}
                card={paymentMethod.card}
                isDeletable={paymentMethods.length > 1}
                isSelected={selectedPaymentMethodId === paymentMethod.id}
                onSelect={() => setSelectedPaymentMethodId(paymentMethod.id)}
                paymentMethodId={paymentMethod.id}
                showButtons
                type={paymentMethod.type}
              />
            ))}
          </Can>
        ) : null}
      </div>

      <Can I="create" a="CompanyPaymentMethod">
        {showAddNewPaymentMethodForm ? (
          <CardForm
            invoiceIds={invoiceIds}
            buttonLabel={t("add_new_payment_method")}
            onSubmit={() => setShowAddNewPaymentMethodForm(false)}
            onCancel={() => setShowAddNewPaymentMethodForm(false)}
          />
        ) : (
          <Button
            onClick={() => {
              setShowUpdatePaymentMethodForm(false);
              setShowAddNewPaymentMethodForm(true);
            }}
            buttonStyle="Blue"
            className={clsxm("h-auto w-fit gap-3 px-8 py-3 sm:min-w-90", actionButtonClassName)}>
            <PlusIcon className="size-3 shrink-0" /> {t("add_new_payment_method")}
          </Button>
        )}
      </Can>
    </>
  );
};

export default PaymentFailedContent;
